import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Sobre() {
  const navigate = useNavigate();

  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'Sobre Nós - N&N Soluções Empresariais | Nossa História e Equipe';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conheça a história da N&N Soluções Empresariais, nossa CEO Vanessa Rosa e a equipe especializada em contabilidade e finanças. Valores, missão e compromisso com a excelência.');
    }

    // Add structured data for about page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre N&N Soluções Empresariais",
      "description": "História, valores e equipe da N&N Soluções Empresariais",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/sobre`,
      "mainEntity": {
        "@type": "Organization",
        "name": "N&N Soluções Empresariais",
        "foundingDate": "2021",
        "founder": {
          "@type": "Person",
          "name": "Vanessa Rosa Carmo Gomes",
          "jobTitle": "CEO e Contadora",
          "alumniOf": "Universidade Estácio de Sá"
        },
        "employee": [
          {
            "@type": "Person",
            "name": "Ana Silva",
            "jobTitle": "Especialista Contábil"
          },
          {
            "@type": "Person",
            "name": "Carlos Santos",
            "jobTitle": "Consultor Financeiro"
          },
          {
            "@type": "Person",
            "name": "Maria Oliveira",
            "jobTitle": "Especialista em RH"
          },
          {
            "@type": "Person",
            "name": "João Costa",
            "jobTitle": "Consultor Fiscal"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Av. Goiás, 606",
          "addressLocality": "Goiânia",
          "addressRegion": "GO",
          "postalCode": "74015-010",
          "addressCountry": "BR"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('AboutPage')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/3e6f8fcc8f2630e876a6bece5f75fd5d/1e6fcf1846e59e804b7269319d1935e6.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Home</button>
              <button onClick={() => navigate('/servicos')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Serviços</button>
              <button onClick={() => navigate('/sobre')} className="font-semibold cursor-pointer" style={{ color: '#72142B' }}>Sobre</button>
              <button onClick={() => navigate('/contato')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Contato</button>
              <button onClick={() => navigate('/agendamento')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Agendamento</button>
            </nav>
            <button 
              onClick={() => navigate('/contato')}
              className="text-white px-6 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Fale Conosco
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20business%20team%20in%20modern%20office%20environment%2C%20diverse%20group%20of%20accounting%20professionals%20working%20together%2C%20warm%20professional%20lighting%2C%20sophisticated%20workspace%20with%20financial%20documents%20and%20computers%2C%20burgundy%20and%20cream%20color%20scheme%2C%20inspiring%20teamwork%20atmosphere%20conveying%20trust%20and%20expertise&width=1920&height=1080&seq=sobre-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#72142B]/80 via-[#72142B]/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm" style={{ backgroundColor: '#72142B', color: '#FAF7F2' }}>
                ✨ Nossa História
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Conheça a
              <span className="block" style={{ color: '#FAEBEA' }}>N&N Soluções</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-4xl mx-auto">
              Uma empresa nascida da paixão por simplificar a contabilidade e ajudar empresários a focar no que realmente importa
            </p>
          </div>
        </div>
      </section>

      {/* Nossa História */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
                Nossa Jornada
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
                Como tudo começou
              </h2>
              <p className="text-xl mb-6 leading-relaxed text-gray-700">
                A N&N Soluções Empresariais nasceu do sonho de transformar a relação entre empresários e a contabilidade. Fundada com o propósito de descomplicar processos financeiros, nossa empresa cresceu baseada em valores sólidos de confiança, transparência e excelência.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Desde o início, nossa missão é clara: oferecer soluções contábeis que permitam aos nossos clientes focar no crescimento de seus negócios, enquanto cuidamos de toda a parte burocrática e fiscal com máxima eficiência.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-xl shadow-lg">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#72142B' }}>2021</div>
                  <div className="text-sm text-gray-600">Ano de fundação</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-lg">
                  <div className="text-3xl font-bold mb-2" style={{ color: '#72142B' }}>100%</div>
                  <div className="text-sm text-gray-600">Satisfação dos clientes</div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20accounting%20office%20interior%20with%20professional%20team%20working%2C%20clean%20organized%20workspace%20with%20computers%20and%20financial%20documents%2C%20warm%20professional%20lighting%2C%20burgundy%20and%20cream%20color%20scheme%2C%20inspiring%20business%20environment%20conveying%20growth%20and%20success&width=600&height=500&seq=nossa-historia&orientation=landscape"
                alt="Nossa História"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left order-2 lg:order-1">
              <img
                src="https://static.readdy.ai/image/3e6f8fcc8f2630e876a6bece5f75fd5d/150d1f847718ce090c6d69051cec3386.jpeg"
                alt="Vanessa Rosa Carmo Gomes - CEO"
                className="w-80 h-96 object-cover rounded-2xl shadow-2xl mx-auto lg:mx-0"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
                Liderança
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
                Vanessa Rosa Carmo Gomes
              </h2>
              <p className="text-xl mb-6 leading-relaxed" style={{ color: '#72142B' }}>
                CEO e Fundadora
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Contadora formada pela Universidade Estácio de Sá, Vanessa é apaixonada por Finanças e dedicada a descomplicar a contabilidade para empresários. Com mais de 3 anos de experiência no mercado, ela lidera nossa equipe com foco na excelência e inovação.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed italic">
                "Acredito que a contabilidade deve ser um facilitador para o crescimento dos negócios, não um obstáculo. Por isso, trabalhamos incansavelmente para oferecer soluções que realmente fazem a diferença na vida dos nossos clientes."
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#FAEBEA' }}>
                    <i className="ri-graduation-cap-line text-xl" style={{ color: '#72142B' }}></i>
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: '#72142B' }}>Formação Acadêmica</div>
                    <div className="text-gray-600">Contabilidade - Universidade Estácio de Sá</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#FAEBEA' }}>
                    <i className="ri-award-line text-xl" style={{ color: '#72142B' }}></i>
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: '#72142B' }}>Especialização</div>
                    <div className="text-gray-600">Gestão Financeira e Consultoria Empresarial</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Valores */}
      <section className="py-20" style={{ backgroundColor: '#FAEBEA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-white px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ color: '#72142B' }}>
              Nossos Valores
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              O que nos move
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Nossos valores fundamentais guiam cada decisão e ação em nossa empresa
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Paixão</h3>
              <p className="text-gray-600 leading-relaxed">
                Amamos o que fazemos e isso se reflete na qualidade dos nossos serviços e no cuidado com cada cliente.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Confiança</h3>
              <p className="text-gray-600 leading-relaxed">
                Construímos relacionamentos duradouros baseados na transparência, honestidade e responsabilidade.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Inovação</h3>
              <p className="text-gray-600 leading-relaxed">
                Buscamos constantemente novas formas de simplificar e otimizar os processos contábeis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#72142B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#FAF7F2' }}>
            Quer fazer parte da nossa história?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto" style={{ color: '#FAEBEA' }}>
            Entre em contato conosco e descubra como podemos ajudar sua empresa a crescer
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contato')}
              className="px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ backgroundColor: '#FAF7F2', color: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Fale conosco
            </button>
            <button 
              onClick={() => navigate('/agendamento')}
              className="border-2 px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold"
              style={{ borderColor: '#FAF7F2', color: '#FAF7F2' }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#FAF7F2';
                e.target.style.color = '#72142B';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#FAF7F2';
              }}
            >
              Agendar consulta
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif', color: '#FAEBEA' }}>
                N&N Soluções Empresariais
              </h3>
              <p className="text-gray-300 mb-6">
                Simplificando sua contabilidade para você focar no que realmente importa.
              </p>
              <div className="space-y-2">
                <p className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-3"></i>
                  WhatsApp: (62) 99391-1090
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-3"></i>
                  contato@nnsolucoes.com.br
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-map-pin-line mr-3"></i>
                  Av. Goiás, 606 - Setor Central, Goiânia - GO
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => navigate('/servicos')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Serviços</button></li>
                <li><button onClick={() => navigate('/sobre')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Sobre</button></li>
                <li><button onClick={() => navigate('/contato')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Contato</button></li>
                <li><button onClick={() => navigate('/agendamento')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Agendamento</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Nossos Serviços</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-300">Contabilidade</span></li>
                <li><span className="text-gray-300">BPO Financeiro</span></li>
                <li><span className="text-gray-300">Departamento Pessoal</span></li>
                <li><span className="text-gray-300">Consultoria Financeira</span></li>
                <li><span className="text-gray-300">Declaração de Impostos</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-300">
              © 2024 N&N Soluções Empresariais. Todos os direitos reservados. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-gray-100 ml-1 cursor-pointer" style={{ color: '#FAEBEA' }}>
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
